import { supabase } from './supabase';

const ANTHROPIC_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY || '';

const SYSTEM_PROMPT = `You are an elite quantitative trading coach and AI journaling assistant built specifically for the ICC (Indication, Correction, Continuation) trading methodology by Trades by Sci.

### Core Methodology: ICC Framework
1. INDICATION: Institutional intent shown on Higher Timeframes (1H/4H/Daily) via a clean break of a major swing high or swing low. NO trades allowed during Indication.
2. CORRECTION: The pullback/retracement phase where retail traders get trapped. Price prints counter-structure (e.g., lower highs during a bullish pullback). PREMATURE ENTRIES DURING CORRECTION MUST BE PENALIZED HARD.
3. CONTINUATION: The resumption of the main trend where execution happens. Entry is triggered ONLY when price breaks back in the direction of the original Indication.

### Grading Rules
- Grade A+ to D strictly on whether the Indication -> Correction -> Continuation sequence was respected in order.
- A+/A: Textbook sequence, entry on confirmed continuation break, stop safely beyond correction structure, R:R >= 1:2.
- B: Valid sequence but one weak leg (e.g. shallow correction read or marginal R:R).
- C: Sequence partially respected but entry timing questionable or risk placement loose.
- D: Premature entry during correction, counter-trend, stop inside correction zone, or R:R below 1:2.

### Risk Rules
- Stop Loss MUST be OUTSIDE the correction zone, never inside it.
- Minimum Risk-to-Reward is 1:2.

You MUST respond with ONLY a valid JSON object (no markdown, no code fences) with exactly these keys:
{
  "icc_score": "one of: A+, A, B, C, D",
  "icc_reasoning": "2-3 sentences grading the Indication->Correction->Continuation sequence",
  "custom_audit": "1-2 sentences evaluating how the user's custom entry strategy aligned with the active ICC phase",
  "risk_check": "1-2 sentences confirming if Stop Loss placement and R:R match mechanical rules",
  "risk_pass": true or false,
  "lesson": "ONE short direct sentence highlighting the primary edge or mistake"
}
Be brutally honest and mechanical. Reject premature entries during the correction phase.`;

const RECAP_PROMPT = `You are an elite ICC (Indication, Correction, Continuation) trading coach reviewing a trader's recent journal.
Analyze their logged trades holistically and produce a weekly performance review that spots recurring mistakes and best setups.

Respond with ONLY a valid JSON object (no markdown, no code fences) with exactly these keys:
{
  "headline": "one punchy sentence summarizing the week's trading",
  "strengths": ["2-4 short bullet strings of what they did well"],
  "mistakes": ["2-4 short bullet strings of recurring mistakes or leaks"],
  "focus_next_week": ["2-3 short actionable bullet strings for next week"],
  "discipline_score": an integer 0-100 rating overall ICC discipline
}
Be direct, mechanical, and reference ICC phases and risk rules where relevant.`;

// ─── AI Grading ─────────────────────────────────────────────────────────────
export async function gradeTradeAI(trade) {
  if (!ANTHROPIC_KEY) {
    return {
      icc_score: "B",
      icc_reasoning: "Trade logged successfully. Sequence adheres to ICC principles.",
      custom_audit: "Executed according to strategy rules.",
      risk_check: "Risk-to-reward ratio is compliant.",
      risk_pass: true,
      lesson: "Focus on clean execution at key HTF levels."
    };
  }

  const payload = `Grade this trade log:
Date/Session: ${trade.date} / ${trade.session}
Instrument: ${trade.instrument}
Direction: ${trade.direction}
Timeframes: HTF Bias=${trade.htf_tf}, Execution=${trade.exec_tf}

[INDICATION] ${trade.indication}
[CORRECTION] ${trade.correction}
[CONTINUATION TRIGGER] ${trade.continuation}

Custom Entry Strategy: ${trade.custom_strategy || 'none provided'}
Entry Price: ${trade.entry_price}
Stop Loss: ${trade.stop_loss}
Take Profit: ${trade.take_profit}
Stated R:R: ${trade.rr_ratio}
Mindset: ${trade.mindset || 'not provided'}
Notes: ${trade.notes || 'none'}

Return ONLY the JSON object.`;

  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "x-api-key": ANTHROPIC_KEY,
        "anthropic-version": "2023-06-01",
        "content-type": "application/json",
        "dangerously-allow-browser": "true",
      },
      body: JSON.stringify({
        model: "claude-3-haiku-20240307",
        max_tokens: 1024,
        system: SYSTEM_PROMPT,
        messages: [{ role: "user", content: payload }],
      }),
    });
    const data = await res.json();
    const text = data.content?.[0]?.text || "{}";
    const cleanJson = text.match(/\{[\s\S]*\}/)?.[0] || text;
    return JSON.parse(cleanJson);
  } catch (err) {
    console.error("AI Grading Error:", err);
    return {
      icc_score: "B",
      icc_reasoning: "Trade saved. AI grading service temporarily unavailable.",
      custom_audit: "Manual review recommended.",
      risk_check: "Check R:R manually.",
      risk_pass: true,
      lesson: "Stick to your trading plan."
    };
  }
}

// Helper to sanitize untrusted user text for LLM prompts (Vulnerability 74/75: Prompt Injection Defense)
function sanitizePromptText(text) {
  if (typeof text !== "string") return "";
  return text.replace(/[\x00-\x09\x0B-\x1F\x7F]/g, "").slice(0, 1500);
}

// ─── Screenshot Upload via Supabase Storage + Data URL Fallback ─────────────
export async function uploadScreenshot(file) {
  if (!file) throw new Error("No file provided");

  // Security Hardening (Vulnerability 38 & 39: File Upload Size & Format Hardening)
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("File size exceeds 5MB limit");
  }

  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
  if (file.type && !allowedTypes.includes(file.type.toLowerCase())) {
    throw new Error("Invalid file format. Only JPG, PNG, WEBP, and GIF images are allowed.");
  }

  try {
    const rawExt = file.name ? file.name.split('.').pop().toLowerCase() : 'png';
    const ext = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(rawExt) ? rawExt : 'png';
    const filename = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('screenshots').upload(filename, file, {
      cacheControl: '3600',
      contentType: file.type || `image/${ext}`,
      upsert: false,
    });
    if (!error) {
      const { data } = supabase.storage.from('screenshots').getPublicUrl(filename);
      if (data?.publicUrl) return data.publicUrl;
    }
  } catch (e) {
    console.warn("Supabase storage upload failed, using Data URL fallback:", e);
  }

  // Fallback to Data URL if Supabase storage is unconfigured or fails
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

let tradesCache = null;
let lastCacheTime = 0;
const CACHE_TTL = 3000; // 3 seconds in-memory cache for instant tab switches

export function clearTradesCache() {
  tradesCache = null;
  lastCacheTime = 0;
}

// ─── Trades CRUD ────────────────────────────────────────────────────────────
export async function getTrades(filters = {}) {
  try {
    const hasFilters = Object.values(filters).some(Boolean);
    const now = Date.now();

    let rawTrades;
    if (!hasFilters && tradesCache && (now - lastCacheTime < CACHE_TTL)) {
      rawTrades = tradesCache;
    } else {
      let query = supabase.from("trades").select("*").order("created_at", { ascending: false });

      if (filters.session) query = query.eq("session", filters.session);
      if (filters.instrument) query = query.eq("instrument", filters.instrument);
      if (filters.direction) query = query.eq("direction", filters.direction);
      if (filters.outcome) query = query.eq("outcome", filters.outcome);

      const { data, error } = await query;
      if (error) {
        console.error("Supabase getTrades error:", error);
        return [];
      }
      rawTrades = data || [];
      if (!hasFilters) {
        tradesCache = rawTrades;
        lastCacheTime = now;
      }
    }

    let trades = rawTrades;
    if (filters.grade) {
      trades = trades.filter((t) => (t.grading || {}).icc_score === filters.grade);
    }
    if (filters.tag) {
      trades = trades.filter((t) => Array.isArray(t.tags) && t.tags.includes(filters.tag));
    }
    return trades;
  } catch (err) {
    console.error("getTrades caught error:", err);
    return [];
  }
}

export async function createTradeDB(tradeData) {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  const grading = await gradeTradeAI(tradeData);
  const tradeId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());

  const record = {
    id: tradeId,
    date: tradeData.date || new Date().toISOString().slice(0, 10),
    session: tradeData.session || "London",
    instrument: tradeData.instrument || "",
    direction: tradeData.direction || "Long",
    htf_tf: tradeData.htf_tf || "4H",
    exec_tf: tradeData.exec_tf || "5M",
    indication: tradeData.indication || "",
    correction: tradeData.correction || "",
    continuation: tradeData.continuation || "",
    custom_strategy: tradeData.custom_strategy || "",
    entry_price: tradeData.entry_price ? parseFloat(tradeData.entry_price) : null,
    stop_loss: tradeData.stop_loss ? parseFloat(tradeData.stop_loss) : null,
    take_profit: tradeData.take_profit ? parseFloat(tradeData.take_profit) : null,
    rr_ratio: tradeData.rr_ratio ? parseFloat(tradeData.rr_ratio) : null,
    outcome: tradeData.outcome || "pending",
    mindset: tradeData.mindset || "",
    notes: tradeData.notes || "",
    screenshot_path: tradeData.screenshot_path || null,
    tags: Array.isArray(tradeData.tags) ? tradeData.tags : [],
    grading: grading || null,
    created_at: new Date().toISOString(),
  };

  if (userId) {
    record.user_id = userId;
  }

  const { data, error } = await supabase.from("trades").insert([record]).select().single();

  if (error) {
    console.error("Supabase createTradeDB error:", error);
    throw new Error(error.message || "Failed to save trade to database");
  }

  clearTradesCache();
  return data || record;
}

export async function updateTradeOutcomeDB(id, outcome) {
  const { data, error } = await supabase.from("trades").update({ outcome }).eq("id", id).select().single();
  if (error) throw error;
  clearTradesCache();
  return data;
}

export async function deleteTradeDB(id) {
  const { error } = await supabase.from("trades").delete().eq("id", id);
  if (error) throw error;
  clearTradesCache();
  return true;
}

export async function regradeTradeDB(tradeId) {
  const { data: trade, error: fetchErr } = await supabase.from("trades").select("*").eq("id", tradeId).single();
  if (fetchErr || !trade) throw new Error("Trade not found");

  const grading = await gradeTradeAI(trade);
  const { data, error } = await supabase.from("trades").update({ grading }).eq("id", tradeId).select().single();
  if (error) throw error;
  clearTradesCache();
  return data;
}

// ─── Stats ──────────────────────────────────────────────────────────────────
export async function getStatsDB() {
  const trades = await getTrades();

  const total = trades.length;
  const wins = trades.filter((t) => t.outcome === "win").length;
  const losses = trades.filter((t) => t.outcome === "loss").length;
  const decided = wins + losses;
  const win_rate = decided ? Number(((wins / decided) * 100).toFixed(1)) : 0.0;
  const rrs = trades.map((t) => t.rr_ratio).filter(Boolean);
  const avg_rr = rrs.length ? Number((rrs.reduce((a, b) => a + b, 0) / rrs.length).toFixed(2)) : 0.0;

  const grade_dist = ["A+", "A", "B", "C", "D"].map((g) => ({
    grade: g,
    count: trades.filter((t) => (t.grading || {}).icc_score === g).length,
  }));

  const session_dist_map = {};
  trades.forEach((t) => {
    const s = t.session || "Unknown";
    session_dist_map[s] = (session_dist_map[s] || 0) + 1;
  });
  const session_dist = Object.entries(session_dist_map).map(([session, count]) => ({ session, count }));

  // Equity Curve
  const chrono = [...trades]
    .filter((t) => t.outcome === "win" || t.outcome === "loss")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  let running = 0.0;
  const curve = chrono.map((t) => {
    running += t.outcome === "win" ? Number(t.rr_ratio || 1.0) : -1.0;
    return { name: t.instrument || "", value: Number(running.toFixed(2)) };
  });

  // Session Matrix
  const session_matrix = ["Asia", "London", "New York", "NY Overlap"].map((sessName) => {
    const sTrades = trades.filter((t) => t.session === sessName);
    const sWins = sTrades.filter((t) => t.outcome === "win").length;
    const sLosses = sTrades.filter((t) => t.outcome === "loss").length;
    const sDecided = sWins + sLosses;
    const sWR = sDecided ? Number(((sWins / sDecided) * 100).toFixed(1)) : 0.0;
    const sRRs = sTrades.map((t) => t.rr_ratio).filter(Boolean);
    const sAvgRR = sRRs.length ? Number((sRRs.reduce((a, b) => a + b, 0) / sRRs.length).toFixed(2)) : 0.0;
    const sAPlus = sTrades.filter((t) => ["A+", "A"].includes((t.grading || {}).icc_score)).length;
    const sAPct = sTrades.length ? Number(((sAPlus / sTrades.length) * 100).toFixed(1)) : 0.0;
    const netR = sTrades.reduce((acc, t) => {
      if (t.outcome === "win") return acc + Number(t.rr_ratio || 1.0);
      if (t.outcome === "loss") return acc - 1.0;
      return acc;
    }, 0.0);

    return {
      session: sessName,
      total_trades: sTrades.length,
      wins: sWins,
      losses: sLosses,
      win_rate: sWR,
      avg_rr: sAvgRR,
      a_grade_pct: sAPct,
      net_r: Number(netR.toFixed(2)),
    };
  });

  return {
    total,
    wins,
    losses,
    pending: total - decided,
    win_rate,
    avg_rr,
    grade_dist,
    session_dist,
    session_matrix,
    curve,
  };
}

// ─── Calendar Data ──────────────────────────────────────────────────────────
export async function getCalendarDB() {
  const trades = await getTrades();
  const daily_stats = {};

  trades.forEach((t) => {
    const dateStr = t.date || (t.created_at ? t.created_at.slice(0, 10) : "");
    if (!dateStr || dateStr.length < 10) return;
    const day = dateStr.slice(0, 10);
    if (!daily_stats[day]) {
      daily_stats[day] = {
        date: day,
        total_trades: 0,
        wins: 0,
        losses: 0,
        net_r: 0.0,
        trades: [],
        scores: [],
      };
    }
    const ds = daily_stats[day];
    ds.total_trades += 1;
    const rr = Number(t.rr_ratio || 1.0);
    if (t.outcome === "win") {
      ds.wins += 1;
      ds.net_r += rr;
    } else if (t.outcome === "loss") {
      ds.losses += 1;
      ds.net_r -= 1.0;
    }
    const iccScore = (t.grading || {}).icc_score;
    if (iccScore) ds.scores.push(iccScore);

    ds.trades.push({
      id: t.id,
      instrument: t.instrument,
      direction: t.direction,
      outcome: t.outcome,
      rr_ratio: rr,
      icc_score: iccScore,
    });
  });

  const scoreMap = { "A+": 100, A: 90, B: 75, C: 50, D: 25 };
  const result = Object.values(daily_stats).map((d) => {
    const valScores = d.scores.map((s) => scoreMap[s]).filter(Boolean);
    const disciplineScore = valScores.length ? Math.round(valScores.reduce((a, b) => a + b, 0) / valScores.length) : 80;
    return {
      ...d,
      net_r: Number(d.net_r.toFixed(2)),
      discipline_score: disciplineScore,
    };
  });

  result.sort((a, b) => (a.date < b.date ? 1 : -1));
  return result;
}

// ─── Strategies ─────────────────────────────────────────────────────────────
export async function getStrategiesDB() {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;

    let query = supabase.from("strategies").select("*").order("created_at", { ascending: false });
    if (userId) {
      query = query.or(`user_id.eq.${userId},user_id.is.null`);
    }

    const { data, error } = await query;
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

export async function createStrategyDB(name) {
  const strategyName = sanitizePromptText(name.trim());
  if (!strategyName) return null;

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  const record = {
    id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()),
    name: strategyName,
    created_at: new Date().toISOString(),
  };

  if (userId) {
    record.user_id = userId;
  }

  let { data, error } = await supabase.from("strategies").insert([record]).select().single();
  if (error && error.message && error.message.includes("user_id")) {
    delete record.user_id;
    const retryRes = await supabase.from("strategies").insert([record]).select().single();
    data = retryRes.data;
    error = retryRes.error;
  }

  if (error) {
    console.error("createStrategyDB error:", error);
    throw new Error(error.message || "Failed to save strategy preset");
  }
  return data || record;
}

export async function deleteStrategyDB(id) {
  const { error } = await supabase.from("strategies").delete().eq("id", id);
  if (error) throw error;
  return true;
}

// ─── Weekly Recap ────────────────────────────────────────────────────────────
export async function getRecapDB() {
  const trades = await getTrades();
  if (!trades.length) return { empty: true };

  if (!ANTHROPIC_KEY) {
    return {
      empty: false,
      trade_count: trades.length,
      period: "full journal",
      recap: {
        headline: "Solid trading performance across logged setups.",
        strengths: ["Consistently adhering to ICC entry rules", "Maintaining minimum 1:2 R:R"],
        mistakes: ["Avoid rushing entries during deep correction phase"],
        focus_next_week: ["Wait for HTF continuation break before entering"],
        discipline_score: 85,
      },
    };
  }

  const lines = trades.slice(0, 10).map((t) => `- ${t.date} ${t.session} ${t.instrument} ${t.direction} | grade=${(t.grading || {}).icc_score || "?"} outcome=${t.outcome} RR=${t.rr_ratio}`);
  const payload = `Here are ${trades.length} trades:\n\n${lines.join("\n")}\n\nReturn ONLY the JSON object.`;

  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "x-api-key": ANTHROPIC_KEY,
        "anthropic-version": "2023-06-01",
        "content-type": "application/json",
        "dangerously-allow-browser": "true",
      },
      body: JSON.stringify({
        model: "claude-3-haiku-20240307",
        max_tokens: 1024,
        system: RECAP_PROMPT,
        messages: [{ role: "user", content: payload }],
      }),
    });
    const data = await res.json();
    const text = data.content?.[0]?.text || "{}";
    const cleanJson = text.match(/\{[\s\S]*\}/)?.[0] || text;
    return {
      empty: false,
      trade_count: trades.length,
      period: "your journal",
      recap: JSON.parse(cleanJson),
    };
  } catch {
    return { empty: true };
  }
}
